/**
 * Sprint 26.0 — <TaskDetail mount="pane" | "page">. Document-centric (body-first).
 *
 * ONE component, two container layouts: the pane is the default (slide-over over
 * the snapshot); the same component mounts full-page at /tasks/:id for deep-links
 * / mobile. Pane = single-column stack + a details FOOTER; page = two-column
 * (content + details SIDEBAR).
 *
 * The detail LEADS WITH THE BODY — the body IS the task (the document). Section
 * order: status/rank chip + title → Body (hero document) → Workflow (advance) →
 * Sub-tasks + References → Details → Activity/history → Continuous Improvement.
 * `instructions` stays as a separate agent-facing field. The body is the only
 * work-content field — there is no separate short summary.
 */

import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import {
  useTaskDetail,
  useTaskHistory,
  useUpdateTaskField,
  useMoveTaskToTop,
  useTriageTask,
  useDropTask,
  useCreateTask,
} from "../../hooks/use-task-detail";
import { useRunRetrospective, useApplyLearnings, type CheckLearningsResult } from "../../hooks/use-retrospective";
import { ActionMenu, type ActionMenuItem } from "../shell/ActionMenu";
import { PaneStatusPill } from "./PaneStatusPill";
import { EditableTitle } from "./EditableTitle";
import { BodySection } from "./BodySection";
import { InstructionsSection } from "./InstructionsSection";
import { ReferencesSection } from "./ReferencesSection";
import { RelatedSection } from "../scope/RelatedSection";
import { SubtasksSection } from "./SubtasksSection";
import { TaskDetailMeta } from "./TaskDetailMeta";
import { RecentlyTimeline } from "../agent-profile/RecentlyTimeline";
import { relativeTime } from "../../lib/describe-recent-event";
import type { Task } from "../../../lib/types";

export interface TaskDetailProps {
  mount: "pane" | "page";
  agentId: string;
  agentName: string;
  /** Edit mode — the task to show. Omit for create mode. */
  taskId?: string;
  /** Optional seed so the pane renders instantly while the query loads. */
  seed?: Task;
  createMode?: boolean;
  onClose?: () => void;
  onCreated?: (taskId: string) => void;
}

export function TaskDetail(props: TaskDetailProps) {
  if (props.createMode) return <CreateModeDetail {...props} />;
  return <EditModeDetail {...props} />;
}

// ---------------------------------------------------------------------------
// Edit mode
// ---------------------------------------------------------------------------

function EditModeDetail({ mount, agentId, agentName, taskId, seed, onClose }: TaskDetailProps) {
  const navigate = useNavigate();
  const detailQuery = useTaskDetail(taskId);
  const historyQuery = useTaskHistory(taskId);
  const update = useUpdateTaskField();
  const moveToTop = useMoveTaskToTop();
  const triage = useTriageTask();
  const drop = useDropTask();

  const task: Task | undefined = detailQuery.data?.task ?? seed;
  if (!taskId || !task) {
    return <div style={loading}>Loading task…</div>;
  }

  const subtasks = detailQuery.data?.subtasks ?? [];
  const history = historyQuery.data ?? detailQuery.data?.history ?? [];
  const ownerName = task.agent_name ?? agentName;
  const ownerId = task.agent_id ?? agentId;
  const patch = (fields: Record<string, unknown>) => update.mutate({ agentId: ownerId, taskId, fields });

  const isTerminal = task.status === "done" || task.status === "dropped";

  // Occasional lifecycle actions live in the header kebab, not a primary action
  // bar. "Move to top" is the one reorder verb (= make this current / rank #1).
  const lifecycleItems: ActionMenuItem[] = [];
  if (!isTerminal) {
    lifecycleItems.push({ label: "Move to top", icon: "arrow-bar-to-up", onClick: () => moveToTop.mutate({ taskId }) });
    if (task.status === "inbox") {
      lifecycleItems.push({ label: "Triage to queue", icon: "list", onClick: () => triage.mutate({ taskId }) });
    }
    lifecycleItems.push({
      label: "Drop",
      icon: "trash",
      variant: "danger",
      onClick: () => {
        if (window.confirm(`Drop "${task.title}"?`)) drop.mutate({ taskId });
      },
    });
  }

  return (
    <div style={mount === "page" ? pageGrid : paneStack} data-testid="task-detail" data-mount={mount}>
      {/* Left / main column */}
      <div style={{ minWidth: 0 }}>
        {mount === "pane" && (
          <Header
            agentName={ownerName}
            onOpenFull={() => navigate(`/tasks/${taskId}`)}
            onClose={onClose}
          />
        )}

        <div style={statusRow}>
          <PaneStatusPill task={task} />
          <span style={metaText}>on {ownerName} · created {relativeTime(task.created_at)}</span>
          {lifecycleItems.length > 0 && (
            <span style={{ marginLeft: "auto" }}>
              <ActionMenu items={lifecycleItems} />
            </span>
          )}
        </div>

        <EditableTitle value={task.title} onSave={(t) => t.trim() && patch({ title: t.trim() })} />
        <div style={titleHelper}>Click title to edit</div>

        {/* Body-first — the document IS the task. */}
        <BodySection value={task.body ?? ""} onSave={(b) => patch({ body: b })} />

        {/* №22 — the OPERATIONAL execution thread (▶ Play, interject, escalation) lives
            in /app (the desk Work plane → /app/task/:id), NOT the console. The console
            is the maker/config surface. See routes/app/TaskThreadPage. */}

        <SubtasksSection subtasks={subtasks} />
        <ReferencesSection taskId={taskId} agentId={ownerId} />

        {/* Sprint 26.2 — cross-tag: what connects to this task across the workspace
            (other agents' activities logged about it, content that mentions it). */}
        <RelatedSection subjectKind="task" subjectId={taskId} />


        <InstructionsSection value={task.instructions ?? ""} onSave={(i) => patch({ instructions: i })} />

        {mount === "pane" && (
          <TaskDetailMeta task={task} ownerName={ownerName} mount="pane" onUpdate={patch} />
        )}

        <section style={{ marginBottom: 16 }}>
          <RecentlyTimeline recent={history} agentName={ownerName} />
        </section>

        <ContinuousImprovementSection task={task} />
      </div>

      {/* Right column — page mount only (the Details sidebar) */}
      {mount === "page" && (
        <div style={{ minWidth: 0 }}>
          <TaskDetailMeta task={task} ownerName={ownerName} mount="page" onUpdate={patch} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Continuous Improvement — check_task_against_learnings + compose_retrospective
// ---------------------------------------------------------------------------

function ContinuousImprovementSection({ task }: { task: Task }) {
  const navigate = useNavigate();
  const runRetro = useRunRetrospective();
  const applyLearnings = useApplyLearnings();
  const [lastCheck, setLastCheck] = useState<CheckLearningsResult | null>(null);

  const checkCount = lastCheck?.findings_count ?? task.last_learnings_check_findings_count;
  const checkAt = lastCheck?.checked_at ?? task.last_learnings_check_at;

  return (
    <section style={{ marginBottom: 20 }} data-testid="continuous-improvement-section">
      <div style={ciLabel}>Continuous improvement</div>
      <div style={ciCard}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            style={ciBtn}
            disabled={applyLearnings.isPending}
            onClick={() => applyLearnings.mutate({ task_id: task.id }, { onSuccess: setLastCheck })}
          >
            <i className="ti ti-bulb" style={{ fontSize: 13 }} aria-hidden="true" />
            {applyLearnings.isPending ? "Checking…" : "Apply learnings"}
          </button>
          <button
            type="button"
            style={ciBtn}
            disabled={runRetro.isPending}
            onClick={() => runRetro.mutate({ task_id: task.id })}
          >
            <i className="ti ti-notebook" style={{ fontSize: 13 }} aria-hidden="true" />
            {runRetro.isPending ? "Composing…" : "Compose retrospective"}
          </button>
        </div>

        {checkAt != null && (
          <div style={ciMeta} data-testid="ci-last-check">
            Last checked {relativeTime(checkAt)} · {checkCount ?? 0} relevant {(checkCount ?? 0) === 1 ? "learning" : "learnings"}
          </div>
        )}

        {lastCheck && lastCheck.relevant_memories.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {lastCheck.relevant_memories.map((m) => (
              <button key={m.id} type="button" style={learningRow} onClick={() => navigate(`/library/${m.id}`)}>
                <i className="ti ti-message-circle" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <span style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)" }}>{m.name}</span>
                  {m.snippet && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{m.snippet}</span>}
                </span>
              </button>
            ))}
          </div>
        )}

        {task.retrospective_page_id && (
          <button style={retroLink} onClick={() => navigate(`/library/${task.retrospective_page_id}`)}>
            <i className="ti ti-file-text" style={{ fontSize: 13 }} aria-hidden="true" /> View retrospective
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Create mode — empty pane, title focused
// ---------------------------------------------------------------------------

function CreateModeDetail({ agentId, agentName, onClose, onCreated }: TaskDetailProps) {
  const create = useCreateTask();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [instructions, setInstructions] = useState("");
  const [initialStatus, setInitialStatus] = useState<"inbox" | "queued">("inbox");
  const [busy, setBusy] = useState(false);

  const canCreate = title.trim().length > 0 && !busy;

  async function submit() {
    if (!canCreate) return;
    setBusy(true);
    try {
      const { task } = await create.mutateAsync({
        agent_id: agentId,
        title: title.trim(),
        body: body.trim() || undefined,
        instructions: instructions.trim() || undefined,
        status: initialStatus,
      });
      onCreated?.(task.id);
    } catch {
      setBusy(false);
    }
  }

  return (
    <div style={paneStack} data-testid="task-detail" data-mount="pane" data-create="true">
      <Header agentName={agentName} onClose={onClose} />
      <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)" }}>New task</div>
      <EditableTitle value={title} placeholder="Name this task…" autoFocus onChange={setTitle} onSave={setTitle} />
      <div style={titleHelper}>Title is required.</div>

      <LabeledArea label="Document" value={body} onChange={setBody} placeholder="Write the task — the document is the task." />
      {/* D-L6 (№22.5 ride-along) — the brief is written for a member of staff. */}
      <LabeledArea label="Instructions for the hire" value={instructions} onChange={setInstructions} placeholder="Optional — directive guidance for the hire." accent />

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 6 }}>Initial status</div>
        <div style={{ display: "flex", gap: 14 }}>
          {([["inbox", "Inbox (default)"], ["queued", "Queue"]] as const).map(([val, lbl]) => (
            <label key={val} style={radioLabel}>
              <input type="radio" name="initial-status" checked={initialStatus === val} onChange={() => setInitialStatus(val)} />
              {lbl}
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button type="button" style={ghostBtn} onClick={onClose}>Cancel</button>
        <button type="button" style={{ ...primaryBtn, opacity: canCreate ? 1 : 0.5 }} disabled={!canCreate} onClick={submit} data-testid="create-submit">
          Create
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Header({ agentName, onOpenFull, onClose }: { agentName: string; onOpenFull?: () => void; onClose?: () => void }) {
  return (
    <div style={header}>
      <span style={breadcrumb}>
        {agentName}
        <i className="ti ti-chevron-right" style={{ fontSize: 10 }} aria-hidden="true" />
        Task
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {onOpenFull && (
          <button type="button" style={fullBtn} onClick={onOpenFull}>
            <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden="true" /> Open full page
          </button>
        )}
        <button type="button" style={closeBtn} aria-label="Close pane" title="Close (Esc)" onClick={onClose}>
          <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}

function LabeledArea({ label, value, onChange, placeholder, accent }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; accent?: boolean }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 4 }}>{label}</div>
      <div style={{ position: "relative", borderRadius: "var(--border-radius-md)", overflow: "hidden", border: "0.5px solid var(--color-border-tertiary)", paddingLeft: accent ? 4 : 0 }}>
        {accent && <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: "var(--c-purple-400)" }} aria-hidden="true" />}
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={areaInput} />
      </div>
    </div>
  );
}

const paneStack: CSSProperties = { display: "flex", flexDirection: "column" };
const pageGrid: CSSProperties = { display: "grid", gridTemplateColumns: "2fr 1fr", gap: 28, alignItems: "start" };

const loading: CSSProperties = { fontSize: 13, color: "var(--color-text-tertiary)", padding: 12 };

const header: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
};

const breadcrumb: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const fullBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const closeBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 2,
};

const statusRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 6,
  flexWrap: "wrap",
};

const metaText: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)" };

const titleHelper: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 16, marginTop: 2 };

const primaryBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 14px",
  fontSize: 12,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
};

const ghostBtn: CSSProperties = {
  padding: "7px 14px",
  fontSize: 12,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const areaInput: CSSProperties = {
  width: "100%",
  minHeight: 64,
  padding: "9px 11px",
  fontSize: 13,
  lineHeight: 1.6,
  border: "none",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  resize: "vertical",
  boxSizing: "border-box",
  display: "block",
};

const radioLabel: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const ciLabel: CSSProperties = { fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 6 };

const ciCard: CSSProperties = {
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const ciBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 500,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  cursor: "pointer",
};

const ciMeta: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 8 };

const learningRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "8px 11px",
  background: "transparent",
  cursor: "pointer",
};

const retroLink: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  marginTop: 10,
  fontSize: 12,
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  padding: 0,
};
